import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, Link, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'hooks';
import { login } from 'store/slices/authSlice';
import AuthLayout from './AuthLayout';
import ResendConfirmation from './ResendConfirmation';

const LoginScreen: React.FC = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [needsConfirm, setNeedsConfirm] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const auth = useAppSelector(state => state.auth);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const justConfirmed = searchParams.get('confirmed') === '1';
  const linkError = searchParams.get('error');

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Login to EasyChatDesk';
    return () => {
      document.title = prevTitle;
    };
  }, []);

  if (auth.token) {
    return <Navigate to="/" />;
  }

  const loading = auth.status === 'loading';

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setNeedsConfirm(false);

    const result = await dispatch(login({ username, password }));

    if (login.fulfilled.match(result)) {
      navigate('/');
      window.location.reload();
    } else if (login.rejected.match(result)) {
      const msg = (result.payload as string) || 'Login failed.';
      setErrorMessage(msg);
      if (/confirm/i.test(msg)) setNeedsConfirm(true);
    }
  };

  return (
    <AuthLayout title="Welcome back" subtitle="Log in to your EasyChatDesk dashboard">
      {justConfirmed && (
        <div className="ecd-auth__alert ecd-auth__alert--success">
          Your email is confirmed — please log in.
        </div>
      )}
      {linkError && (
        <div className="ecd-auth__alert ecd-auth__alert--warning">
          That confirmation link is invalid or has expired. Log in to request a new one.
        </div>
      )}
      {auth.status === 'failed' && errorMessage && (
        <div className="ecd-auth__alert ecd-auth__alert--danger">{errorMessage}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="ecd-auth__field">
          <label className="ecd-auth__label" htmlFor="email">
            Email address
          </label>
          <input
            id="email"
            type="email"
            className="ecd-auth__input"
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
          />
        </div>

        <div className="ecd-auth__field">
          <label className="ecd-auth__label" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            type="password"
            className="ecd-auth__input"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="current-password"
          />
        </div>

        {needsConfirm && (
          <div style={{ marginBottom: 16 }}>
            <ResendConfirmation email={username} />
          </div>
        )}

        <button type="submit" className="ecd-auth__btn" disabled={loading}>
          {loading ? 'Signing in…' : 'Log in'}
        </button>
      </form>

      <div className="ecd-auth__hint">
        Not registered?{' '}
        <Link to="/register" className="ecd-auth__link">
          Create an account
        </Link>
      </div>
      <div className="ecd-auth__hint" style={{ marginTop: 6 }}>
        <a
          href="https://easychatdesk.com/recover-your-password/"
          className="ecd-auth__link"
        >
          Lost your password?
        </a>
      </div>
    </AuthLayout>
  );
};

export default LoginScreen;
